import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Ban, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { StatCard } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

export default function SuperAdminOrgDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/super-admin/organisations/${id}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.put(`/super-admin/organisations/${id}/status`, { status }); toast.success(`Organisation ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setPlan = async (plan) => {
    try { await api.put(`/super-admin/organisations/${id}/plan`, { plan }); toast.success("Plan updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (data === null) return <div className="p-8 max-w-5xl"><Skeleton className="h-96 rounded-xl" /></div>;
  if (data === false) return <div className="p-8"><Link to="/super-admin/organisations" className="text-accent">← Back</Link></div>;

  const org = data.organisation;
  const suspended = org.status === "suspended";

  return (
    <div className="p-5 sm:p-8 max-w-5xl">
      <Link to="/super-admin/organisations" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> All organisations</Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><Building2 className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{org.name}</h1>
            <div className="mt-1 flex gap-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${suspended ? "bg-destructive/10 text-destructive" : "bg-accent/10 text-accent"}`}>{org.status}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={org.plan} onValueChange={setPlan}>
            <SelectTrigger className="w-40 capitalize" data-testid="org-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
          </Select>
          {suspended ? (
            <Button onClick={() => setStatus("active")} className="rounded-md bg-accent text-accent-foreground" data-testid="reactivate-org-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Reactivate</Button>
          ) : (
            <Button onClick={() => setStatus("suspended")} variant="destructive" className="rounded-md" data-testid="suspend-org-btn"><Ban className="h-4 w-4 mr-1.5" /> Suspend</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Events" value={data.usage.events} accent />
        <StatCard label="Members" value={data.usage.members} />
        <StatCard label="Attendees" value={data.usage.attendees} />
        <StatCard label="AI reports" value={data.usage.reports} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-lg font-bold text-foreground mb-3">Users</h2>
          <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="org-users">
            {data.users.map((u, i) => (
              <div key={i} className="flex items-center justify-between p-3.5">
                <div><p className="text-sm font-medium text-foreground">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{u.role?.replace("org_", "")}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-foreground mb-3">Events</h2>
          <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="org-events">
            {data.events.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No events.</p> : data.events.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-3.5">
                <p className="text-sm font-medium text-foreground">{e.name}</p>
                <span className="text-xs text-muted-foreground capitalize">{e.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
